import { Grid } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isLenderRole, isReadonlyRole, isTPORole } from "../../../../redux/actions/userAction";
import "../../../AddNewProject/AddNewProject.css";
import { getAdditionalDocuments, getLenderRequestsByProject } from "../../../shared/services/LenderRequestService";
import SuccessPage from "../../../SuccessPage/SuccessPage";
import VerticalLinearStepper from "../../../VerticalLinearStepper/VerticalLinearStepper";
import axios from "axios";
import { fetchRequestStatuses, initLenderRequest, resetLenderRequest, selectRequestSubTypesById } from "../../../../redux/actions/lenderRequestActions";
import AttachmentsPage from "./AttachmentsPage";
import ProjectInfoBanner from "../../../SharedComponents/ProjectInfoBanner/ProjectInfoBanner";
import PhaseInfoBanner from "../../../SharedComponents/PhaseInfoBanner/PhaseInfoBanner";
import { REQUEST_TYPES } from "../../../shared/constants/RequestTypes";
import { REQUEST_STATUSES } from "../../../shared/constants/RequestStatuses";

const AdditionalDocuments = (props) => {
  if((!isLenderRole() && !isTPORole()) || isReadonlyRole()) {
     location.replace("/unauthorized");
     return null;
  }

  const [stepCount, setStepCount] = useState(0);
  const [request, setRequest] = useState();
  const [docRequirements, setDocRequirements] = useState();
  const [projectName, setProjectName] = useState(null);
  const [phaseId, setPhaseId] = useState(null);
  const [phaseName, setPhaseName] = useState(null);

  const steps = [
    "Upload Additional Documents", 
    "Final Confirmation"
  ];

  const incrementCounter = () => setStepCount(stepCount + 1);
  const decrementCounter = () => setStepCount(stepCount - 1);

  const requestId = +props.match.params.request_id;
  const projectId = +props.match.params.project_id;

  const requestSubTypeLookup = useSelector(state => state.lenderRequestReducer.requestSubTypeLookup);
  const requiredDocTypes = useSelector(state => state.lenderRequestReducer.requiredDocTypes);
  const optionalDocTypes = useSelector(state => state.lenderRequestReducer.optionalDocTypes);

  const [optionalDocs, setOptionalDocs] = useState();

  const dispatch = useDispatch();

  useEffect(() => {
    fetchCurrentRequest();
    fetchProjectName();
  }, []);

  async function fetchProjectName() {
    try {
      const { data: project } = await axios.get(
        global.REACT_APP_API_ENDPOINT +
        `/condoMgr/getProjectDetailsByProjectId/` +
        projectId
      );
      if (project?.projectProfile?.projectName) {
        setProjectName(project.projectProfile.projectName);
      }
    } catch (err) {
      console.error("AdditionalDocuments - Error fetching project name:", err);
    }
  }

  useEffect(() => {
    if (request && request.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE) {
      axios.get(
        `${global.REACT_APP_API_ENDPOINT}/lender/request/projectStagingData?masterTrackerId=${requestId}`
      ).then(response => {
        if (response.status === 200) {
          setPhaseId(response.data?.phaseId ?? null);
          setPhaseName(response.data?.phaseName ?? null);
        }
      }).catch(error => {
        console.error("AdditionalDocuments - Error fetching staging data for phase info:", error);
      });
    }
  }, [request]);

  useEffect(() => {
    if (request) {
      // Reset and (re)initialize request type mappings/state used by attachments.
      // initLenderRequest returns the requestSubTypeIds so we can deterministically
      // select the right subtypes before saving attachments.
      (async () => {
        dispatch(resetLenderRequest());
        const requestSubTypeIds = await dispatch(initLenderRequest(request.requestTypeName));
        if (Array.isArray(requestSubTypeIds) && requestSubTypeIds.length) {
          dispatch(selectRequestSubTypesById(requestSubTypeIds));
        } else {
          console.error("AdditionalDocuments - initLenderRequest returned no requestSubTypeIds", {
            requestTypeName: request.requestTypeName,
            requestSubTypeIds,
          });
        }
        // dispatch(fetchRequestStatuses());
      })();
    }
  }, [request]);

  useEffect(() => {
    // requiredDocTypes and optionalDocTypes are populated by selectRequestSubTypesById,
    // which runs after initLenderRequest. We depend on all four so the effect re-runs
    // once the doc-type lists are actually ready (not while they're still null from reset).
    if (request && requestSubTypeLookup && (requiredDocTypes || optionalDocTypes)) {
      // Build the full optional list from the Redux doc-type state populated by
      // initLenderRequest + selectRequestSubTypesById. This covers all doc types
      // associated with this request type and subtype.
      const allDocTypes = [
        ...(requiredDocTypes?.flat() ?? []),
        ...(optionalDocTypes?.flat() ?? []),
      ];
      setOptionalDocs(allDocTypes);

      if (request.internalStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
        // Fetch the specific docs Fannie Mae requested and show them as required.
        // The full doc-type list (above) will populate the Optional documents optgroup.
        (async function() {
          const docRequestEvent = request.requestFeedActivityTrackerDTOList?.findLast(evt =>
            evt.externalStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED &&
            evt.activity.toLowerCase() === "status changed"
          );

          if (docRequestEvent) {
            const additionalDocuments = await getAdditionalDocuments(docRequestEvent.requestActivityTrackerId);
            const nestedRequirements = additionalDocuments.map(doc => ([{
              ...doc,
              name: doc.documentName,
              required: true,
            }]));
            setDocRequirements(nestedRequirements);
          } else {
            // Status is AIR but no matching tracker event found — treat as no required docs.
            setDocRequirements([]);
          }
        })();
      } else {
        // For any other status (e.g. Under Review, Pending Review) all doc types are
        // optional — skip the activity tracker lookup entirely.
        setDocRequirements([]);
      }
    }
  }, [request, requestSubTypeLookup, requiredDocTypes, optionalDocTypes]);

  async function fetchCurrentRequest() {
    const requests = await getLenderRequestsByProject(projectId);
    const currentRequest = requests.find(req => +req.requestId === +requestId);

    if (!currentRequest) {
      console.error("Request not found", requestId, requests);
      throw new Error("Request not found");
    }

    // console.log("currentRequest:", currentRequest);

    setRequest(currentRequest);
  }

  const getPage = () => {
    if (!request || !docRequirements) {
      return <div className="loading">Loading...</div>; 
    }

    if (!stepCount) {
      return (
        <AttachmentsPage
          request={request}
          requirements={docRequirements}
          optional={optionalDocs}
          incrementCounter={incrementCounter}
          decrementCounter={decrementCounter}
        />
      );
    }

    return (
      <SuccessPage
        project_id={projectId}
        requestTypeName={request.requestTypeName}
        msg={"Documentation submitted successfully!"}
      />
    );
  };

  return (
    <Grid className="add-new-project" container>
      <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
        <VerticalLinearStepper
          steps={steps}
          activeStep={stepCount}
        />
      </Grid>
      <Grid
        container
        item
        xs={9}
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        style={{ padding: "20px", border: "solid 1px black" }}
      >
        {request?.requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE ? (
          <PhaseInfoBanner phaseId={phaseId} phaseName={phaseName} />
        ) : (
          <ProjectInfoBanner projectId={projectId} projectName={projectName} />
        )}
        {getPage()}
      </Grid>
    </Grid>
  );
};

export default AdditionalDocuments;
